/**
 * Logging estructurado del front (GR_Linus: todo código entregado lleva logging
 * estructurado).
 *
 * Emite una línea JSON por evento con timestamp y ámbito.
 *
 * ---------------------------------------------------------------------------
 * US-66 (#201) — Soberanía de telemetría (R-PT-10, P1)
 * Endurecido en #409 (US-66 follow-up): el guard pasa de denylist a ALLOWLIST.
 * ---------------------------------------------------------------------------
 * Cualquier analítica/telemetría del front EXCLUYE el contenido de las
 * conversaciones del tenant: aquí solo viajan metadatos técnicos del propio
 * cliente (evento, ids, rol, timings, códigos de error, conteos), NUNCA texto
 * de mensajes, "de qué va", títulos ni nombres reales de chats.
 *
 * Hoy no hay ningún proveedor externo de telemetría: `emit` escribe a la
 * consola del navegador y nada más sale del cliente (ver
 * docs/soberania-telemetria-us66.md). Aun así, este módulo aplica un GUARD
 * defensivo antes de emitir.
 *
 * #409 — El guard es ALLOWLIST (fail-closed), no denylist. En vez de enumerar
 * las claves de contenido a bloquear (con el riesgo de que una clave nueva no
 * listada se cuele), SOLO se permiten las claves de metadato técnico
 * explícitamente autorizadas (`ALLOWED_META_KEYS`); cualquier otra clave se
 * descarta por defecto. Se deja constancia del NOMBRE de lo descartado (nunca
 * el valor) en `_scrubbed`. Así, si mañana se añade un campo con contenido del
 * tenant —o se enchufa un sink externo— queda bloqueado sin depender de que
 * alguien lo recordara "denylistar".
 */

// #409 — ALLOWLIST de metadato técnico (fail-closed). SOLO estas claves pueden
// viajar en un evento de telemetría; cualquier otra se descarta por defecto
// (R-PT-10). Son metadatos del propio cliente —ids técnicos, rol/estado,
// códigos de error, versión de contrato, tiempos y conteos—, nunca contenido
// de conversación del tenant. La comparación es case-insensitive. Ampliar la
// allowlist es explícito y barato: al añadir un metadato técnico nuevo se
// incluye aquí. Si se olvida, el peor caso es que ese metadato no se registre
// (fail-closed), nunca que se filtre contenido del tenant.
const ALLOWED_META_KEYS = new Set([
  // Identificadores técnicos / ámbito (nunca contenido)
  'id', 'chat_id', 'tenant_id', 'user_id', 'event_id', 'session_id',
  'action_id', 'agent_id', 'agente_destino', 'seq',
  // Rol / estado / códigos de error / versión de contrato
  'rol', 'role', 'estado', 'status', 'code', 'from', 'to',
  'declared', 'received', 'contract_version', 'version',
  // Tiempos y conteos
  'ms', 'duration_ms', 'latency_ms', 'elapsed_ms', 'count', 'total', 'n',
  // Correo del destinatario de una invitación de administración (US-26): es un
  // identificador de la acción admin, no contenido de conversación del tenant.
  'email',
].map(k => k.toLowerCase()))

// Aplica la allowlist a un objeto de datos: conserva SOLO las claves permitidas
// y descarta el resto (fail-closed), a cualquier profundidad. Dentro de una
// clave permitida cuyo valor sea objeto/array se re-aplica la allowlist
// (defensa en profundidad). Devuelve { clean, dropped } —dropped solo lleva
// NOMBRES de claves descartadas, nunca sus valores— sin mutar la entrada.
// `depth` acota la recursión ante ciclos o estructuras enormes; al superarla se
// descarta el subárbol (fail-closed) en vez de dejarlo pasar sin filtrar.
function scrubTenantContent(data, depth = 0) {
  const dropped = []
  if (data === null || typeof data !== 'object') {
    return { clean: data, dropped }
  }
  if (depth > 4) {
    return { clean: Array.isArray(data) ? [] : {}, dropped }
  }
  if (Array.isArray(data)) {
    const clean = data.map(v => {
      const r = scrubTenantContent(v, depth + 1)
      dropped.push(...r.dropped)
      return r.clean
    })
    return { clean, dropped }
  }
  const clean = {}
  for (const [key, value] of Object.entries(data)) {
    if (!ALLOWED_META_KEYS.has(key.toLowerCase())) {
      dropped.push(key)
      continue
    }
    const r = scrubTenantContent(value, depth + 1)
    dropped.push(...r.dropped)
    clean[key] = r.clean
  }
  return { clean, dropped }
}

function emit(level, event, data = {}) {
  const { clean, dropped } = scrubTenantContent(data)
  const record = {
    ts: new Date().toISOString(),
    scope: 'portico',
    level,
    event,
    ...clean,
  }
  // R-PT-10: si el guard tuvo que descartar campos, se deja constancia SOLO de
  // los nombres (nunca los valores) para poder corregir el origen sin filtrar
  // nada del tenant.
  if (dropped.length) record._scrubbed = [...new Set(dropped)]
  const line = JSON.stringify(record)
  // eslint-disable-next-line no-console
  ;(console[level] ?? console.log)(line)
}

export const logEvent = (event, data) => emit('info', event, data)
export const logWarn  = (event, data) => emit('warn', event, data)
export const logError = (event, data) => emit('error', event, data)

// Exportado para test/lint del criterio de soberanía (US-66/#409). No es API de
// uso general: valida que solo viajan metadatos técnicos de la allowlist.
export { scrubTenantContent, ALLOWED_META_KEYS }
